import { useState, useEffect } from 'react';
import { EditorState, ContentState } from 'draft-js';
import dayjs from 'dayjs';
import convertFromHTML from 'html-to-draftjs';
import { ConfigGroup, ConfigurationState } from '../types/configuration';
import { parseTimeSpan, findSelectedItem } from '../utils/configurationUtils';
import dataProvider from '../../services/api';

interface UseConfigurationDataProps {
  showSuccessMessage: (message: string) => void;
  showErrorMessage: (message: string) => void;
  handleErrorWithLogout: (error: any) => void;
}

export const useConfigurationData = ({
  showSuccessMessage,
  showErrorMessage,
  handleErrorWithLogout,
}: UseConfigurationDataProps) => {
  const [configGroups, setConfigGroups] = useState<ConfigGroup[]>([]);
  const [selectedItem, setSelectedItem] = useState<string>('');
  const [configState, setConfigState] = useState<ConfigurationState>({
    selectedDays: '0',
    selectedTime: dayjs().set('hour', 0).set('minute', 0).set('second', 0),
    selectedValue: '',
    editorState: EditorState.createEmpty(),
    htmlValue: '',
  });
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const fetchConfigurations = async () => {
      try {
        const { data } = await dataProvider.getConfigurations('configurations');
        if (!data || !Array.isArray(data)) {
          throw new Error('Invalid configuration data received from the server');
        }

        const sanitizedData = data.map((group: ConfigGroup) => {
          if (!group.items || !Array.isArray(group.items)) {
            throw new Error(`Invalid items in group ${group.name}`);
          }
          return {
            ...group,
            items: group.items || [],
          };
        });

        setConfigGroups(sanitizedData);
        if (sanitizedData.length > 0 && sanitizedData[0].items.length > 0) {
          setSelectedItem(sanitizedData[0].items[0].id);
        } else {
          showErrorMessage('No valid configuration found.');
        }
      } catch (error: any) {
        handleErrorWithLogout(error);
      }
    };
    fetchConfigurations();
  }, [dataProvider ]);

  useEffect(() => {
    const fetchSelectedValue = async () => {
      if (selectedItem) {
        const item = findSelectedItem(configGroups, selectedItem);
        if (item && item.apiName) {
          try {
            const { data } = await dataProvider.getConfigurationValue({ name: item.apiName });
            const currentGroup = configGroups.find(group => group.items.some(i => i.id === selectedItem));
            
            if (currentGroup && currentGroup.name === 'Email Templates') {
              const rawHtml = data.value || '';
              const contentBlocks = convertFromHTML(rawHtml);
              const contentState = ContentState.createFromBlockArray(contentBlocks.contentBlocks);
              
              setConfigState({
                ...configState,
                htmlValue: rawHtml,
                editorState: EditorState.createWithContent(contentState),
                selectedValue: data.value || '',
              });
            } else if (item.dataType === 'TimeSpan') {
              const [days, hours, minutes, seconds] = parseTimeSpan(data.value || '0');
              setConfigState({
                ...configState,
                selectedDays: days.toString(),
                selectedTime: dayjs().set('hour', hours).set('minute', minutes).set('second', seconds),
              });
            } else if (item.dataType === 'Boolean') {
              setConfigState({
                ...configState,
                selectedDays: '0',
                selectedTime: dayjs().set('hour', 0).set('minute', 0).set('second', 0),
                selectedValue: data.value.toLowerCase(),
              });
            } else {
              setConfigState({
                ...configState,
                selectedDays: '0',
                selectedTime: dayjs().set('hour', 0).set('minute', 0).set('second', 0),
                selectedValue: data.value || '',
              });
            }
          } catch (error: any) {
            handleErrorWithLogout(error);
            setConfigState({
              selectedDays: '0',
              selectedTime: dayjs().set('hour', 0).set('minute', 0).set('second', 0),
              selectedValue: '',
              editorState: EditorState.createEmpty(),
              htmlValue: '',
            });
          }
        }
      }
    };
    fetchSelectedValue();
  }, [selectedItem, dataProvider, configGroups]);

  return {
    configGroups,
    selectedItem,
    setSelectedItem,
    configState,
    setConfigState,
    isUpdating,
    setIsUpdating,
  };
};